import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MarketButton } from '@market/react';
import { LogoIcon } from 'svgs';
import {
  ContactInformationIdentifierType,
  IdentifierType,
} from 'routes/profile/models/Identifier';
import IdentifierValueInput from 'components/IdentifierValueInput';
import PhoneInput from 'routes/native-sign-in/components/PhoneInput';
import ErrorToastSlot from 'components/ErrorToastSlot';
import { isValidEmail } from 'utils/identifiers';
import { createReceiptLookupVerification } from 'services/buyerportal';

type Props = {
  onCodeSent: (
    identifierType: ContactInformationIdentifierType,
    identifierValue: string
  ) => void;
};

/**
 * First step of the account-less receipt lookup: collects an email or phone and sends an OTP.
 * Modeled on privacy-requests/delete/RequestDeleteData.
 */
const ReceiptLookupOtpRequest: React.FC<Props> = ({ onCodeSent }) => {
  const { t } = useTranslation();

  const [identifierType, setIdentifierType] =
    useState<ContactInformationIdentifierType>(IdentifierType.Email);
  const [identifierValue, setIdentifierValue] = useState<string>('');
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [isUnexpectedError, setIsUnexpectedError] = useState<boolean>(false);
  const [isPhoneValid, setIsPhoneValid] = useState<boolean>(false);
  const [isEmailValid, setIsEmailValid] = useState<boolean>(false);
  const [
    shouldDisplayInvalidSubmissionError,
    setShouldDisplayInvalidSubmissionError,
  ] = useState<boolean>(false);

  const isValueValid = () =>
    identifierType === IdentifierType.Phone ? isPhoneValid : isEmailValid;

  const onSetIdentifierType = (type: ContactInformationIdentifierType) => {
    setIdentifierType(type);
    setIsPhoneValid(false);
    setIsEmailValid(false);
    setShouldDisplayInvalidSubmissionError(false);
  };

  const sendCode = async () => {
    if (!isValueValid()) {
      setShouldDisplayInvalidSubmissionError(true);
      return;
    }

    try {
      setIsProcessing(true);
      await createReceiptLookupVerification(identifierType, identifierValue);
      onCodeSent(identifierType, identifierValue);
    } catch {
      setIsUnexpectedError(true);
    } finally {
      setIsProcessing(false);
    }
  };

  const inputComponent =
    identifierType === IdentifierType.Email ? (
      <IdentifierValueInput
        identifierType={identifierType}
        identifierValue={identifierValue}
        onIdentifierTypeChanged={onSetIdentifierType}
        onIdentifierValueChanged={(value) => {
          setIdentifierValue(value);
          setIsEmailValid(isValidEmail(value));
          setShouldDisplayInvalidSubmissionError(false);
        }}
        isInvalid={shouldDisplayInvalidSubmissionError}
        phoneInputDataTestId={'phone-input'}
        emailInputDataTestId={'email-input'}
        onEnterPressed={sendCode}
      />
    ) : (
      <PhoneInput
        onChange={(isValid: boolean, number: string) => {
          setIdentifierValue(number);
          setIsPhoneValid(isValid);
          setShouldDisplayInvalidSubmissionError(false);
        }}
        onEnterPressed={sendCode}
        shouldShowValidationError={shouldDisplayInvalidSubmissionError}
        value={identifierValue}
      />
    );

  return (
    <div className={'flex justify-center mt-2 p-6'}>
      <main className={'flex flex-col max-w-[440px]'}>
        <LogoIcon className={'text-[32px] mb-2'} />
        <h1 className={'heading-30'}>{t('receiptLookup.otp.request.title')}</h1>
        <p className={'paragraph-30 mt-0 mb-6'}>
          {t('receiptLookup.otp.request.subtitle')}
        </p>

        {inputComponent}

        <MarketButton
          onClick={sendCode}
          disabled={!isValueValid() || isProcessing}
          rank={'primary'}
        >
          {t('common.sendCode')}
        </MarketButton>

        <MarketButton
          rank={'tertiary'}
          disabled={isProcessing}
          onClick={() =>
            onSetIdentifierType(
              identifierType === IdentifierType.Email
                ? IdentifierType.Phone
                : IdentifierType.Email
            )
          }
        >
          {identifierType === IdentifierType.Email
            ? t('common.usePhoneNumber')
            : t('common.useEmailAddress')}
        </MarketButton>

        <ErrorToastSlot
          errored={isUnexpectedError}
          clearError={() => setIsUnexpectedError(false)}
        />
      </main>
    </div>
  );
};

export default ReceiptLookupOtpRequest;
